/**
 * The help desk's contact (M39), where people get stuck: under the sign-in
 * form and at the end of a check's result. Nothing is shown when the
 * deployment sets no contact, or while it is being fetched.
 */
import { ExternalLink, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

import { reachable, telHref, useHelpContact } from "@/api/help";
import { cn } from "@/lib/utils";

const LINK = "inline-flex items-center gap-1.5 font-medium text-primary underline";

export function HelpContact({ title, className }: { title: string; className?: string }) {
  const { t } = useTranslation();
  const contact = useHelpContact().data;
  if (!reachable(contact)) {
    return null;
  }
  return (
    <section
      aria-label={t("help.label")}
      className={cn("flex flex-col gap-1.5 text-sm", className)}
    >
      <p>{title}</p>
      <ul className="flex flex-wrap gap-x-5 gap-y-1">
        {contact.email ? (
          <li>
            <a className={LINK} href={`mailto:${contact.email}`}>
              <Mail aria-hidden="true" className="size-4" />
              {contact.email}
            </a>
          </li>
        ) : null}
        {contact.phone ? (
          <li>
            <a className={LINK} href={telHref(contact.phone)}>
              <Phone aria-hidden="true" className="size-4" />
              {contact.phone}
            </a>
          </li>
        ) : null}
        {contact.url ? (
          <li>
            <a className={LINK} href={contact.url} target="_blank" rel="noreferrer">
              <ExternalLink aria-hidden="true" className="size-4" />
              {t("help.page")} <span className="sr-only">{t("help.newTab")}</span>
            </a>
          </li>
        ) : null}
      </ul>
      {contact.hours ? <p className="text-muted-foreground">{contact.hours}</p> : null}
    </section>
  );
}
